// App
import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

// Legacy controller
import { getFaqSchemaTemplate } from 'app/shared/modules/FaqSection/controller';

// Components
import AsyncFaqSection from 'app/shared/modules/MobileSRP/FaqSection';
import AsyncDemographics from 'app/shared/modules/MobileSRP/Demographics';
import AsyncMarketHighlights from 'app/shared/modules/MobileSRP/MarketHighlights';
import AsyncArticlesAndFaq from 'app/shared/modules/MobileSRP/ArticlesAndFaq';
import AsyncDenverAreaFacts from 'app/shared/modules/area/AreaFacts';
import SrpContentWrapper from 'app/shared/modules/MobileSRP/__BaseComponents__/SrpContentWrapper';

// Misc
import { font } from 'app/shared/styles/_fontsize';
import { DENVER_QUICK_ANSWER } from 'app/shared/utils/quickAnswersV2';

const StyledSnapshotText = styled.p`
  ${font.lg};
  font-weight: bold;
  padding-top: 16px;
  padding-bottom: 16px;
`;

type Area = {
  name: string;
  resourceId: string;
};

type Query = {
  page?: string | number;
};

type RootState = {
  filter: {
    search: {
      slug: string;
    };
  };
  area: {
    area: Area;
    facts: Record<string, unknown>;
    factsByBeds: Record<string, unknown>;
  };
  listings: {
    totalListings: number;
  };
  location: {
    current: {
      query: Query;
    };
  };
};

/**
 * This component serves as a wrapper that encloses:
 * - Reviews
 * - FAQ section
 * - Demographics
 * - Market highlights
 * - Articles
 * - Denver Area Facts - a custom component requested by our SEO consultants for content testing
 *
 * For SEO purposes, these components are hidden or visible depending on
 * the current search slug or availability of content
 */
const AreaInformationWrapper = () => {
  // Redux
  const searchSlug = useSelector((state: RootState) => state.filter.search.slug);
  const area = useSelector((state: RootState) => state.area.area);
  const facts = useSelector((state: RootState) => state.area.facts);
  const factsByBeds = useSelector((state: RootState) => state.area.factsByBeds);
  const totalListings = useSelector((state: RootState) => state.listings.totalListings);
  const query = useSelector((state: RootState) => state.location.current.query);

  const hasPageParam = query.page && Boolean(query.page);

  if (hasPageParam && Number(query.page) > 1) {
    return null;
  }

  const getSchemaData = (slug: string) => {
    const areaTemplateData = {
      area,
      facts,
      factsByBeds,
      totalListings,
    };

    return getFaqSchemaTemplate({ searchSlug: slug, data: areaTemplateData });
  };

  const faqSectionData = getSchemaData(searchSlug);

  return (
    <>
      <StyledSnapshotText>{area.name} Snapshot</StyledSnapshotText>
      {faqSectionData ? (
        <SrpContentWrapper identifier={'faq-section'} title={'Frequently asked questions'}>
          <AsyncFaqSection faqData={faqSectionData as unknown as never} questionHeadingLevel="h3" />
        </SrpContentWrapper>
      ) : (
        <>
          <AsyncDemographics />
          <AsyncMarketHighlights />
        </>
      )}
      {searchSlug === 'apartments-for-rent' && area.resourceId !== 'denver-co' && <AsyncArticlesAndFaq />}
      {searchSlug === 'apartments-for-rent' && area.resourceId === 'denver-co' && (
        <AsyncDenverAreaFacts facts={DENVER_QUICK_ANSWER as unknown as never} />
      )}
    </>
  );
};

export default AreaInformationWrapper;
